// Das Modell des Lesewegs (ST-SEA-001, erster Schnitt).
//
// REINES MODELL, kein React, kein DOM -- dieselbe Trennung wie in
// quick-capture.ts und aus demselben Grund: was hier steht, ist ohne Browser
// pruefbar, und in dieser Umgebung gibt es keinen.
//
// ZWEI ZUSAGEN, die hier und nicht im Bildschirm eingeloest werden:
//
//   1. KEIN TECHNIKBEGRIFF ERREICHT DEN BILDSCHIRM (NFR-UX-003, CM-UX-010).
//      Die API antwortet mit `lifecycleStatus: 'draft'`, `kind: 'person'` und
//      `verificationStatus: 'unverified'`. Auf dem Bildschirm steht "Entwurf",
//      "Person" und "nicht bestätigt". Die Uebersetzung ist eine Tabelle und
//      keine Bedingungskette, damit ein unbekannter Wert einen benannten
//      Rueckfall bekommt statt eines leeren Feldes.
//
//   2. DIE OBERFLAECHE FILTERT NICHTS. Sie zeigt, was die API liefert. Die
//      Sichtbarkeitsregel steht serverseitig in
//      apps/api/src/domain/party/party-reader.ts; eine zweite Fassung hier
//      waere die zweite Wahrheit, die CM-SEC-002 ausschliesst -- und die
//      gefaehrlichere, weil sie im Browser laeuft.

import { correlationOf, plainMessageFor, type PlainMessage, type ResponseFacts } from './access.ts';
import { artWort, hausArtOf, type HausArt } from './haus-art.ts';

/** Dieselbe Einspritzstelle wie in quick-capture.ts: Proben ohne Netz. */
export type SendLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface PartySummary {
  readonly partyId: string;
  readonly kind: string;
  readonly lifecycleStatus: string;
  readonly displayName: string;
  /**
   * Nur bei einem Kennungstreffer der Suche (ST-SEA-001 zweiter Schnitt): die
   * Kennung, die den Treffer erklaert. Ein Namenstreffer erklaert sich durch
   * den Namen in der Zeile und traegt dieses Feld nicht.
   */
  readonly matchedIdentifier?: MatchedIdentifier;
  /** Nur, wenn allein der Rechtsname der Organisation getroffen hat (der Anzeigename ist der Handelsname). */
  readonly matchedLegalName?: string;
  /**
   * Der eine Kontaktweg je Kanal fuer die Zeile (ST-SEA-001, Schnitt
   * "Anzeige", Teil B): der bevorzugte, sonst der einzige. Fehlt er, gibt es
   * keinen eindeutigen -- die Zeile sagt dann nichts, statt zu raten.
   */
  readonly primaryEmailAddress?: string;
  readonly primaryPhoneNumber?: string;
  /**
   * Die sichtbare Organisation einer Person: die aelteste aktive
   * Kante "arbeitet fuer", dieselbe Sichtbarkeit wie im Kontaktdetail. Fehlt
   * sie, gibt es keine sichtbare.
   */
  readonly worksFor?: { readonly partyId: string; readonly displayName: string };
  /** Die Person arbeitet fuer weitere sichtbare Organisationen. */
  readonly worksForMore?: boolean;
  /** Nur in der Suche: die Organisation, ueber deren Namen die Person gefunden wurde. */
  readonly matchedViaOrganization?: string;
  /**
   * Nur bei Ort/PLZ: die Organisation, ueber deren Anschrift die Person getroffen hat (E3 S1);
   * fehlt, wenn die eigene Anschrift trifft. moreCount zaehlt nur sichtbare weitere Organisationen.
   */
  readonly matchedViaOrganizationAddress?: { readonly organizationPartyId: string; readonly displayName: string; readonly moreCount: number };
  /** Die letzte Aenderung irgendwo am Kontakt, soweit sichtbar (ISO-Zeitpunkt). */
  readonly lastChangedAt?: string;
}

/** Die Arten, auf die Liste und Suche einengen koennen (Umschalter der Kontaktliste). */
export type PartyKindFilter = 'person' | 'organization';

/** Der Wert eines `{ value }`-Objekts der Listenantwort, sonst nichts. */
function contactValueOf(value: unknown): string | undefined {
  const inner = (value ?? {}) as Record<string, unknown>;
  return typeof inner.value === 'string' && inner.value.length > 0 ? inner.value : undefined;
}

export interface MatchedIdentifier {
  readonly identifierType: string;
  readonly value: string;
  /** Die Kennung gilt nicht mehr -- der Treffer fuehrt trotzdem zum Kontakt (CM-PTY-004). */
  readonly ended: boolean;
}

/**
 * Die Regeln der Suche, die der Bildschirm VOR dem Senden prueft.
 *
 * Dieselben Zahlen wie in apps/api/src/domain/party/party-search.ts. Die
 * Oberflaeche ist nie die einzige Kontrolle; sie ist die freundlichere: ein
 * Feldfehler mit stehender Eingabe statt eines 422.
 */
export const MINIMUM_SEARCH_TERM_LENGTH = 3;
export const MAXIMUM_SEARCH_TERM_LENGTH = 100;

/**
 * Der Feldhinweis der Suche -- EIN Text fuer Bildschirm und Probe.
 *
 * Er nennt die Mindestlaenge nicht als nackte Zahl, sondern mit dem Grund,
 * den die Nutzerin nachvollziehen kann (NFR-UX-003, CM-UX-010): zwei Zeichen
 * treffen fast alles und die Antwort dauert. Was der Code haelt, steht drin;
 * was er nicht kann (Aehnlichkeit), fehlt bewusst (Pflichtenheft 5.2 Nr. 14).
 */
export const SEARCH_FIELD_HINT =
  'Mindestens drei Zeichen, damit die Suche schnell antwortet und nicht fast alles trifft. Groß- und Kleinschreibung, Umlaute und Bindestriche spielen keine Rolle. Eine Kennung muss genau stimmen.';

/**
 * Der Satz fuer eine 422-Ablehnung des Servers -- EINE Stelle fuer die Grenzen
 * in Worten. Bis zum 15.09.2026 stand hier "ab zwei" als fester Text, und er
 * blieb beim Wechsel auf drei stehen (Experience-Review 15.09.2026, Befund H1):
 * eine Zahl, die an zwei Stellen von Hand gepflegt wird, laeuft auseinander.
 * Die Probe in parties.test.ts liest den Satz gegen die Konstanten.
 */
export const SEARCH_LIMITS_SENTENCE = `LIAISON sucht erst ab ${wortZahl(MINIMUM_SEARCH_TERM_LENGTH)} und bis höchstens ${wortZahl(MAXIMUM_SEARCH_TERM_LENGTH)} Zeichen.`;

/** Zahl in Worten fuer die Saetze der Suche; ausserhalb der Tabelle steht die Ziffer. */
function wortZahl(n: number): string {
  const worte: Record<number, string> = { 1: 'einem', 2: 'zwei', 3: 'drei', 4: 'vier', 5: 'fünf', 100: 'hundert' };
  return worte[n] ?? String(n);
}

/**
 * Prueft den Begriff. `undefined` heisst brauchbar; sonst der Satz fuer das Feld.
 * Der Begriff wird getrimmt geprueft, wie der Server es tut.
 */
export function searchTermProblem(raw: string): string | undefined {
  const term = raw.trim();
  if (term.length < MINIMUM_SEARCH_TERM_LENGTH) {
    // Problem und Loesung in einem Satz (NFR-UX-005); der Grund steht im
    // Feldhinweis darueber und wird hier nicht wiederholt.
    return `Gib mindestens ${wortZahl(MINIMUM_SEARCH_TERM_LENGTH)} Zeichen ein.`;
  }
  if (term.length > MAXIMUM_SEARCH_TERM_LENGTH) {
    return 'Kürze den Suchbegriff auf höchstens hundert Zeichen.';
  }
  return undefined;
}

/**
 * Der zuletzt gesuchte Begriff -- im FLUECHTIGEN Anwendungszustand.
 *
 * WARUM NICHT IN DER ADRESSE: der Begriff ist ein Personendatum. In einer
 * Abfragezeichenkette landete er in Zugriffsprotokollen, im Browserverlauf und
 * in jedem Referrer (ADR-0044 Ziffer 3, NFR-PRV-001).
 *
 * WARUM NICHT IM SITZUNGSSPEICHER, obwohl die erste Fassung ihn dort hielt:
 * `sessionStorage` ueberlebt die Abmeldung im selben Tab. Meldet sich danach
 * ein anderes Konto an, saehe es den Namen oder die Kennung, die die
 * Vorgaengerin gesucht hat -- und der Bildschirm schickte den Begriff mit dem
 * NEUEN Token los. Befund des Codex-Erstreviews vom 03.09.2026 (P1), mit
 * Recht: ein Personendatum darf die Identitaetsgrenze nicht ueberschreiten.
 *
 * Eine Modulvariable lebt genau so lange wie die geladene Anwendung: sie
 * traegt den Begriff ueber den Zurueck-Weg aus dem Kontaktdetail (echter Link
 * wie Zurueck-Knopf, beides bleibt in der Anwendung), stirbt mit jedem
 * vollstaendigen Seitenwechsel -- die Abmeldung ist einer (logoutRedirect) --
 * und wird bei der Abmeldung zusaetzlich ausdruecklich geleert
 * (forgetSearchTerm in main.tsx), damit die Zusage nicht am Weg der Abmeldung
 * haengt. Preis: ein Neuladen vergisst die Suche. Das ist die richtige Seite
 * des Irrtums (ADR-0049 Ziffer 2).
 */
let searchTermInMemory: string | undefined;
let searchFilterInMemory: SearchFilter | undefined;

export function recallSearchTerm(): string | undefined {
  return searchTermInMemory;
}

export function rememberSearchTerm(term: string | undefined): void {
  searchTermInMemory = term === undefined || term.trim().length === 0 ? undefined : term;
}

/** Bei der Abmeldung: nichts darf das naechste Konto im selben Tab wiederfinden. */
export function forgetSearchTerm(): void {
  searchTermInMemory = undefined;
  searchFilterInMemory = undefined;
}

// --- Die Facetten der Suche (ST-SEA-001, CM-SEA-003) -------------------------

/**
 * Die Filter der Suche, wie die Oberflaeche sie fuehrt. Zwischen den Facetten
 * gilt UND, in einer Facette reicht ein Wert. Der Zustand ist nur ein
 * Modifikator: ohne ausdrueckliches Einbeziehen sind Inaktive ausgeblendet
 * (Voreinstellung, E3).
 */
export interface SearchFilter {
  readonly taxonomyTermIds: readonly string[];
  readonly operatingContextId?: string | undefined;
  readonly roleCodes: readonly string[];
  /** So wie eingegeben; leer heisst "nicht gesetzt". Gesucht wird in deutschen Anschriften (Voreinstellung des Servers). */
  readonly postalCodePrefix: string;
  readonly locality: string;
  readonly includeInactive: boolean;
  /** Nur Personen oder nur Organisationen; ohne Angabe alle. Eine Ansicht, kein Suchgrund. */
  readonly kind?: PartyKindFilter | undefined;
  /**
   * "Ansprechpartner:innen von <Organisation>" (E3 S2): Personen mit aktiver
   * Ansprechpartner-Kante (arbeitet fuer, frei, leitet) zu dieser Organisation. Ein Suchgrund. Der Name steht fuer
   * den Chip da und geht nie an den Server.
   */
  readonly contactPersonOf?: { readonly partyId: string; readonly displayName: string } | undefined;
  /**
   * Namen und Achsen von Stichwoertern, die im Suchfeld getippt und ueber die
   * Wertquelle der Suche aufgeloest wurden (ST-SEA-002, PQL S1). Die Auswahl
   * im Filterbereich kennt hoechstens fuenfzig Begriffe; ohne diese Namen
   * stuende ein Chip fuer einen anderen Begriff ohne Namen da. Geht nie an den Server.
   */
  readonly termNames?: Readonly<Record<string, { readonly name: string; readonly axis: string }>> | undefined;
}

export const EMPTY_SEARCH_FILTER: SearchFilter = Object.freeze({
  taxonomyTermIds: Object.freeze([]) as readonly string[],
  roleCodes: Object.freeze([]) as readonly string[],
  postalCodePrefix: '',
  locality: '',
  includeInactive: false,
});

/** Hoechstens so viele Stichwoerter je Suche -- dieselbe Grenze wie der Server (MAXIMUM_FILTER_TERMS). */
export const MAXIMUM_FILTER_TERMS = 10;

/** Die Zustaende, die ohne "Inaktive einbeziehen" gesucht werden. */
export const DEFAULT_LIFECYCLE_STATUSES = Object.freeze(['draft', 'active'] as const);

/** Dieselben Grenzen wie der Server (party-search-filter.ts). */
export const MAXIMUM_POSTAL_CODE_PREFIX_LENGTH = 5;
export const MINIMUM_LOCALITY_LENGTH = 2;
export const MAXIMUM_LOCALITY_LENGTH = 100;

export interface AddressFilterProblems {
  readonly postalCodePrefix?: string | undefined;
  readonly locality?: string | undefined;
}

/**
 * Die Formfehler von PLZ und Ort, als Satz am Feld. Geprueft wird vor dem
 * Senden, damit die Suche nicht erst am Server scheitert und die Eingabe
 * stehen bleibt (NFR-UX-005).
 */
export function addressFilterProblems(filter: SearchFilter): AddressFilterProblems {
  const plz = filter.postalCodePrefix.trim();
  const ort = filter.locality.trim();
  return {
    postalCodePrefix:
      plz.length === 0 || new RegExp(`^[0-9]{1,${MAXIMUM_POSTAL_CODE_PREFIX_LENGTH}}$`).test(plz)
        ? undefined
        : `Die PLZ besteht nur aus Ziffern, höchstens ${MAXIMUM_POSTAL_CODE_PREFIX_LENGTH}, etwa 80 oder 80331.`,
    locality:
      ort.length === 0 || ort.length >= MINIMUM_LOCALITY_LENGTH
        ? ort.length > MAXIMUM_LOCALITY_LENGTH
          ? `Der Ort hat höchstens ${MAXIMUM_LOCALITY_LENGTH} Zeichen.`
          : undefined
        : `Gib für den Ort mindestens ${MINIMUM_LOCALITY_LENGTH} Zeichen ein.`,
  };
}

/** Traegt der Filter einen Suchgrund? Nur dann darf der Begriff fehlen -- dieselbe Regel wie der Server. */
export function filterNarrowsSearch(filter: SearchFilter): boolean {
  return (
    filter.taxonomyTermIds.length > 0 ||
    filter.operatingContextId !== undefined ||
    filter.roleCodes.length > 0 ||
    filter.postalCodePrefix.trim().length > 0 ||
    filter.locality.trim().length > 0 ||
    filter.contactPersonOf !== undefined
  );
}

/** Der Rumpfteil `filter`. Der Zustandsfilter geht immer mit, ausser Inaktive sind einbezogen. */
export function filterBody(filter: SearchFilter): Readonly<Record<string, unknown>> {
  const contextRole =
    filter.operatingContextId === undefined && filter.roleCodes.length === 0
      ? undefined
      : {
          ...(filter.operatingContextId === undefined ? {} : { operatingContextId: filter.operatingContextId }),
          ...(filter.roleCodes.length === 0 ? {} : { roleCodes: [...filter.roleCodes] }),
        };
  const plz = filter.postalCodePrefix.trim();
  const ort = filter.locality.trim();
  const address =
    plz.length === 0 && ort.length === 0
      ? undefined
      : { ...(plz.length === 0 ? {} : { postalCodePrefix: plz }), ...(ort.length === 0 ? {} : { locality: ort }) };
  return {
    ...(filter.taxonomyTermIds.length === 0 ? {} : { taxonomyTermIds: [...filter.taxonomyTermIds] }),
    ...(contextRole === undefined ? {} : { contextRole }),
    ...(address === undefined ? {} : { address }),
    ...(filter.includeInactive ? {} : { lifecycleStatuses: [...DEFAULT_LIFECYCLE_STATUSES] }),
    ...(filter.kind === undefined ? {} : { kind: filter.kind }),
    ...(filter.contactPersonOf === undefined ? {} : { contactPersonOfPartyId: filter.contactPersonOf.partyId }),
  };
}

/** Wie viele Filter wirken -- fuer den Knopf und die Zusammenfassung. "Ohne Inaktive" zaehlt nicht. */
export function activeFilterCount(filter: SearchFilter): number {
  return (
    filter.taxonomyTermIds.length +
    (filter.operatingContextId === undefined ? 0 : 1) +
    filter.roleCodes.length +
    (filter.postalCodePrefix.trim().length === 0 ? 0 : 1) +
    (filter.locality.trim().length === 0 ? 0 : 1) +
    (filter.contactPersonOf === undefined ? 0 : 1)
  );
}

export function recallSearchFilter(): SearchFilter | undefined {
  return searchFilterInMemory;
}

/** Derselbe fluechtige Zustand wie der Begriff: PLZ und Ort folgen, und die sind Personendaten. */
export function rememberSearchFilter(filter: SearchFilter | undefined): void {
  searchFilterInMemory = filter;
}

/**
 * Die Zusammenfassung einer Suche MIT Trefferzahl (erste Seite). Ohne Zahl --
 * etwa ein aelterer Server -- bleibt es beim Satz der geladenen Zeilen.
 */
export function matchSummary(
  term: string | undefined,
  filterCount: number,
  loaded: number,
  hasMore: boolean,
  matchCount: number | undefined,
  matchCountCapped: boolean,
  inactiveHidden = false,
): string {
  const worauf = [
    ...(term === undefined ? [] : [`„${term}“`]),
    ...(filterCount === 0 ? [] : [filterCount === 1 ? '1 Filter' : `${filterCount} Filtern`]),
  ].join(' und ');
  if (matchCount === undefined) {
    if (term !== undefined && filterCount === 0) {
      return searchSummary(term, loaded, hasMore);
    }
    const kontakte = loaded === 1 ? '1 Kontakt passt' : `${loaded} Kontakte passen`;
    return hasMore ? `${kontakte} zu ${worauf}, weitere sind vorhanden.` : `${kontakte} zu ${worauf}.`;
  }
  if (matchCount === 0) {
    // Wer einen inaktiven Kontakt sucht, soll erfahren, warum er fehlt.
    // Der naechste Schritt steht als Knopf "Auch inaktive zeigen" direkt darunter.
    const inaktive = inactiveHidden ? ' Inaktive Kontakte sind ausgeblendet.' : '';
    return filterCount === 0
      ? `Kein Kontakt passt zu ${worauf}. Prüfe die Schreibweise, oder lege den Kontakt an.${inaktive}`
      : `Kein Kontakt passt zu ${worauf}. Entferne einen Filter, um mehr zu finden.${inaktive}`;
  }
  if (matchCountCapped) {
    return `Mehr als ${MATCH_COUNT_CAP_LABEL} Kontakte passen zu ${worauf}. Engere Filter zeigen weniger.`;
  }
  return matchCount === 1 ? `1 Kontakt passt zu ${worauf}.` : `${matchCount.toLocaleString('de-DE')} Kontakte passen zu ${worauf}.`;
}

const MATCH_COUNT_CAP_LABEL = '1.000';

/**
 * Die Kurzform der Ergebniszeile (DESIGN F4 zu L13-01): „12 Kontakte“, ueber
 * dem Deckel „Mehr als 1.000 Kontakte“. Kein „+“ und kein „geladen“; ob weitere
 * Seiten folgen, sagt die Zeile daneben („weitere vorhanden“).
 */
export function resultCountLabel(count: number, capped: boolean): string {
  if (capped) return `Mehr als ${MATCH_COUNT_CAP_LABEL} Kontakte`;
  if (count === 0) return 'Keine Kontakte';
  return count === 1 ? '1 Kontakt' : `${count.toLocaleString('de-DE')} Kontakte`;
}

export interface ContactPointView {
  /**
   * Der Schluessel der ZEILE, und nicht `contactPointId`.
   *
   * Ein Contact Mechanism ist wiederverwendbar (ADR-0008 Punkt 1): dieselbe
   * Adresse kann fuer dieselbe Party unter zwei Verwendungszwecken gelten, und
   * die API liefert dann ZWEI Zeilen mit derselben `contactPointId`. Als
   * React-Schluessel benutzt, waeren das doppelte Schluessel unter
   * Geschwistern -- und die Abstimmung duerfte Beschriftung und Kennzeichen
   * der falschen Verwendung wiederverwenden.
   */
  readonly contactPointUsageId: string;
  readonly contactPointId: string;
  readonly value: string;
  readonly usageCode: string;
  readonly isPreferred: boolean;
  readonly verificationStatus: string;
  readonly sourceSystem: string;
  /**
   * Der Stand der Zeile fuer das optimistische Sperren (CM-UX-004).
   *
   * Er wird NIE angezeigt -- er ist ein Technikbegriff und haette auf einem
   * Bildschirm nichts zu suchen (NFR-UX-003). Er wird mitgeschickt: ohne ihn
   * kann der Server einen gleichzeitigen Konflikt nicht erkennen, und
   * "Konflikte werden nicht still ueberschrieben" waere eine Zusage ohne
   * Durchsetzung.
   */
  readonly lockVersion: number;
  /** Der Sperrstand des Kontaktwegs selbst: ihn schickt die Korrektur des Werts mit (gilt fuer alle Zwecke). */
  readonly contactPointLockVersion: number;
}

/** Eine beendete Verwendung -- die Historie aus CM-PTY-006. */
export interface EndedContactPointView {
  readonly contactPointUsageId: string;
  readonly contactPointId: string;
  readonly value: string;
  readonly usageCode: string;
  readonly endedAt: string;
  readonly endReasonCode?: string;
}

export interface PartyDetail {
  readonly partyId: string;
  readonly kind: string;
  readonly lifecycleStatus: string;
  readonly displayName: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** Der Stand der Party fuer die Statusbefehle; fehlt, wenn die Antwort ihn nicht traegt. */
  readonly lockVersion?: number;
  readonly person?: { readonly givenName?: string; readonly familyName?: string; readonly lockVersion: number };
  readonly organization?: { readonly legalName: string; readonly tradeName?: string; readonly lockVersion: number };
  readonly emailAddresses: readonly ContactPointView[];
  readonly phoneNumbers: readonly ContactPointView[];
  readonly endedEmailAddresses: readonly EndedContactPointView[];
  readonly endedPhoneNumbers: readonly EndedContactPointView[];
  /** Die Website (Kanal web, Signatur S2, X2); dieselben Felder wie E-Mail. Eine aeltere API ohne das Feld ergibt keine. */
  readonly websites: readonly ContactPointView[];
  readonly endedWebsites: readonly EndedContactPointView[];
  readonly endedContactPointsTruncated: boolean;
  /**
   * Gueltige Kontaktwege, die die Antwort wegen ihrer Groessengrenze nicht
   * mitgeschickt hat. Getrennt von `otherContactPointCount`: jener zaehlt
   * KANAELE, die diese Fassung nicht darstellt, dieser die MENGE, die nicht
   * in eine Antwort passt.
   */
  readonly additionalContactPointCount: number;
  readonly otherContactPointCount: number;
  /**
   * Wer den Kontakt sieht (ADR-0064). Optional, weil die Anzeige erst mit dem
   * Web-Anteil von S3k kommt; ownerHouseName ist null bei einem gemeinsamen
   * Kontakt und bei einem stillgelegten Haus, ownerHouseKind die Art (M4, #732).
   */
  readonly visibility?: { readonly scope: 'groupwide' | 'house'; readonly ownerHouseName: string | null; readonly ownerHouseKind?: HausArt | null };
}

export type PartyListResult =
  | {
      readonly outcome: 'loaded';
      readonly parties: readonly PartySummary[];
      readonly hasMore: boolean;
      readonly nextCursor?: string;
    }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export type PartyDetailResult =
  | { readonly outcome: 'loaded'; readonly party: PartyDetail }
  /**
   * EIN AUSGANG FUER ZWEI LAGEN, weil die API sie bewusst nicht unterscheidet:
   * "gibt es nicht" und "darfst du nicht sehen" sind beide 404. Der Bildschirm
   * darf daraus keine Vermutung machen -- ein Satz wie "vielleicht fehlt dir
   * eine Berechtigung" waere genau die Auskunft, die der gleiche Statuscode
   * verweigert.
   */
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

/**
 * Fachbegriffe fuer den Lebenszyklus (CM-PTY-008, NFR-UX-003).
 *
 * Die Werte stammen aus der CHECK-Liste in db/migrations/0002; die
 * Uebersetzung ist die fachliche Lesart und keine Abschrift. `draft` heisst
 * "Entwurf" und nicht "Draft": ein Zustand, den die Schnellerfassung erzeugt,
 * muss in derselben Sprache erscheinen wie der Bildschirm, der ihn erzeugt hat.
 */
export const LIFECYCLE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  draft: 'Entwurf',
  active: 'Aktiv',
  inactive: 'Inaktiv',
  deceased: 'Verstorben',
  dissolved: 'Aufgelöst',
  archived: 'Archiviert',
  merged: 'Zusammengeführt',
  anonymized: 'Anonymisiert',
  deleted: 'Gelöscht',
});

export const KIND_LABELS: Readonly<Record<string, string>> = Object.freeze({
  person: 'Person',
  organization: 'Organisation',
});

export const VERIFICATION_LABELS: Readonly<Record<string, string>> = Object.freeze({
  // Angezeigt erst, wenn Bestätigen möglich ist (bestaetigungPilleSichtbar).
  unverified: 'nicht bestätigt',
  verified: 'bestätigt',
  invalid: 'ungültig',
});

/**
 * Das Usage-Vokabular -- seit dem 01.09.2026 BESCHLOSSEN.
 *
 * Hier stand bis dahin "das Vokabular ist NICHT freigegeben, genau ein Wert ist
 * bestaetigt". Das war richtig und ist es nicht mehr: Michael Brozat hat als
 * Product Council und Data Owner die Werteliste entschieden, Migration `0033`
 * schreibt sie als CHECK auf, und ADR-0043 Ziffer 8 traegt die Begruendung samt
 * der vier verworfenen Vorschlaege.
 *
 * DIE UEBERSETZUNG IST DIE FACHLICHE LESART UND KEINE ABSCHRIFT. `reception`
 * heisst "Zentrale" und nicht "Empfang eines Anrufs"; `assistant` heisst
 * "Assistenz" und nicht "Assistent", weil die Verwendung den ANSCHLUSS meint
 * und nicht die Person.
 *
 * Der Rueckfall auf den Rohwert bleibt, und er bleibt aus demselben Grund:
 * waechst die Werteliste, erscheint der neue Code sichtbar unuebersetzt, statt
 * dass jemand hier ein Wort erfindet, das niemand beschlossen hat. Eine Probe
 * liest die Liste aus `db/migrations/0033` und faellt, sobald sie waechst.
 */
export const USAGE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  business: 'geschäftlich',
  assistant: 'Assistenz',
  billing: 'Rechnung',
  private: 'privat',
  reception: 'Zentrale',
});

/**
 * Der Rueckfall fuer einen unbekannten Code.
 *
 * Er nennt den Rohwert und behauptet nichts. Ein leeres Feld waere schlechter:
 * es sieht aus wie "keine Angabe", waehrend es "diese Fassung kennt den Wert
 * nicht" bedeutet.
 */
export function labelFor(table: Readonly<Record<string, string>>, code: string): string {
  return table[code] ?? code;
}

export function lifecycleLabel(code: string): string {
  return labelFor(LIFECYCLE_LABELS, code);
}

export function kindLabel(code: string): string {
  return labelFor(KIND_LABELS, code);
}

/**
 * Wer den Kontakt sieht (ADR-0064), als Satz fuer den Kontaktkopf. Er nennt
 * die Wirkung und nicht die Technik, in derselben Sprache wie der Satz der
 * Schnellerfassung (captureVisibilityNotice); ohne Hausnamen, etwa bei einem
 * stillgelegten Haus, nennt er die Art ("deinen Verlag", A2).
 */
export function visibilityLabel(visibility: NonNullable<PartyDetail['visibility']>): string {
  return visibility.scope === 'groupwide'
    ? 'Für die ganze Gruppe sichtbar'
    : `Nur für ${visibility.ownerHouseName ?? artWort(visibility.ownerHouseKind, 'deinen')} sichtbar`;
}

export function verificationLabel(code: string): string {
  return labelFor(VERIFICATION_LABELS, code);
}

/**
 * Ob die Bestätigungspille an Kontaktweg oder Kennung steht: "nicht
 * bestätigt" trägt heute fast jede Zeile und löst nichts aus; "bestätigt" und
 * "ungültig" bleiben sichtbar.
 */
export function bestaetigungPilleSichtbar(code: string): boolean {
  return code !== 'unverified';
}

export function usageLabel(code: string): string {
  return labelFor(USAGE_LABELS, code);
}

/**
 * Die Produktzeitzone: Datum und Monat stehen fuer alle so, wie sie in der
 * Redaktion gelten, nicht in der Ortszeit des Geraets.
 */
export const PRODUKT_ZEITZONE = 'Europe/Berlin';

/**
 * Der Datenstand in Worten.
 *
 * Ein ISO-Zeitstempel auf einem Bildschirm ist ein Technikbegriff. Formatiert
 * wird mit der Sprachumgebung des Browsers und einem festen Fallback, damit die
 * Probe unter `node --test` denselben Text sieht wie ein Mensch in Hamburg.
 */
export function formatInstant(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  return parsed.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: PRODUKT_ZEITZONE,
  });
}

/**
 * Die Zusammenfassung der Liste fuer die Statusregion.
 *
 * Sie steht in WORTEN und nicht in einer Zahl allein: ein Screenreader liest
 * "3 Kontakte" vor, waehrend eine Zahl neben einer Ueberschrift nur fuer Augen
 * existiert (NFR-ACC-001, CM-UX-002).
 */
export function listSummary(count: number, hasMore: boolean): string {
  if (count === 0) {
    return 'Keine Kontakte gefunden.';
  }
  const kontakte = count === 1 ? '1 Kontakt' : `${count} Kontakte`;
  return hasMore ? `${kontakte} geladen, weitere sind vorhanden.` : `${kontakte} gefunden.`;
}

/**
 * Die Zusammenfassung einer SUCHE fuer die Statusregion.
 *
 * Sie nennt den Begriff, damit ein Screenreader hoert, worauf sich die Zahl
 * bezieht, und bei null Treffern den naechsten Schritt -- ohne eine Berechtigung
 * zu vermuten: was die Sichtbarkeitsregel verbirgt, ist von einem nicht
 * vorhandenen Kontakt nicht zu unterscheiden, und der Satz darf das nicht
 * aufloesen (CM-SEC-003).
 */
export function searchSummary(term: string, count: number, hasMore: boolean): string {
  if (count === 0) {
    return `Kein Kontakt passt zu „${term}“. Prüfe die Schreibweise, oder lege den Kontakt an.`;
  }
  const kontakte = count === 1 ? '1 Kontakt passt' : `${count} Kontakte passen`;
  return hasMore ? `${kontakte} zu „${term}“, weitere sind vorhanden.` : `${kontakte} zu „${term}“.`;
}

/**
 * Warum eine Zeile Treffer ist, wenn es nicht am Namen liegt.
 *
 * Die Bezeichnung der Kennungsart kommt aus der Tabelle der Kennungspflege --
 * "GLN" statt `gln`, "Verkehrsnummer" statt `verkehrsnummer` (NFR-UX-003).
 * Eine beendete Kennung sagt es dazu: sonst saehe ein Treffer ueber eine alte
 * Nummer aus wie einer ueber eine geltende.
 */
export function matchedIdentifierNotice(hit: MatchedIdentifier, typeLabel: (code: string) => string): string {
  const kennung = `Gefunden über Kennung: ${typeLabel(hit.identifierType)} ${hit.value}`;
  return hit.ended ? `${kennung} (beendet)` : kennung;
}

/**
 * Warum eine Organisation Treffer ist, wenn ihr Anzeigename (der Handelsname)
 * den Begriff nicht traegt: der Rechtsname hat getroffen. Ohne den Satz saehe
 * die Nutzerin "Zwei" als Treffer fuer "Zwei Verlag GmbH" und keinen Grund.
 */
export function matchedLegalNameNotice(legalName: string): string {
  return `Gefunden über Rechtsnamen: ${legalName}`;
}

/**
 * Was zusaetzlich vorhanden, aber noch nicht darstellbar ist.
 *
 * Der Zaehler kommt vom Server und meint AUSSCHLIESSLICH Kanaele, die diese
 * Fassung nicht anzeigt -- nie etwas, das eine Berechtigung verbirgt (siehe
 * party-reader.ts). Der Satz sagt deshalb "zeigt diese Ansicht noch nicht" und
 * nicht "du darfst nicht".
 */
/**
 * Der Hinweis auf Kontaktwege, die nicht in die Antwort gepasst haben.
 *
 * Er sagt AUSDRUECKLICH nicht "du darfst nicht" und auch nicht "es gibt nicht
 * mehr": beides waere falsch. Er sagt, dass diese Ansicht sie nicht
 * mitschickt, und nennt die Zahl -- ohne sie waere eine Obergrenze ein
 * stilles Weglassen, und ein vorhandener, berechtigt sichtbarer Kontaktweg
 * verschwaende ohne Meldung.
 */
export function additionalContactPointNotice(count: number): string | undefined {
  if (count <= 0) {
    return undefined;
  }
  return count === 1
    ? 'Ein weiterer Kontaktweg passt nicht mehr in diese Ansicht. Beende nicht mehr benötigte, dann erscheint er.'
    : `${count} weitere Kontaktwege passen nicht mehr in diese Ansicht. Beende nicht mehr benötigte, dann erscheinen sie.`;
}

export function withheldChannelNotice(count: number): string | undefined {
  if (count <= 0) {
    return undefined;
  }
  return count === 1
    ? 'Ein weiterer Kontaktweg ist hinterlegt; diese Ansicht zeigt bisher nur E-Mail, Telefon und Website.'
    : `${count} weitere Kontaktwege sind hinterlegt; diese Ansicht zeigt bisher nur E-Mail, Telefon und Website.`;
}

function factsOf(response: Response, payload: unknown): ResponseFacts {
  const body = (payload ?? {}) as Record<string, unknown>;
  return {
    status: response.status,
    answered: payload !== undefined,
    ...correlationOf(body),
  };
}

function reasonCodeOf(payload: unknown): string | undefined {
  const body = (payload ?? {}) as Record<string, unknown>;
  return typeof body.reasonCode === 'string' ? body.reasonCode : undefined;
}

function matchedIdentifierOf(value: unknown): MatchedIdentifier | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  if (typeof row.identifierType !== 'string' || typeof row.value !== 'string') {
    return undefined;
  }
  return { identifierType: row.identifierType, value: row.value, ended: row.ended === true };
}

function summariesOf(payload: unknown): readonly PartySummary[] {
  const body = (payload ?? {}) as Record<string, unknown>;
  if (!Array.isArray(body.parties)) {
    return [];
  }
  // Feld fuer Feld und nicht per Spread: was die API zusaetzlich schickte,
  // erreicht den Bildschirm nicht. Dieselbe Richtung wie `responseFieldPaths`
  // auf der Serverseite, nur von der anderen Seite her.
  return body.parties.flatMap((entry: unknown) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    if (typeof row.partyId !== 'string' || typeof row.displayName !== 'string') {
      return [];
    }
    const matchedIdentifier = matchedIdentifierOf(row.matchedIdentifier);
    const matchedLegalName =
      typeof row.matchedLegalName === 'string' && row.matchedLegalName.length > 0 ? row.matchedLegalName : undefined;
    const primaryEmailAddress = contactValueOf(row.primaryEmailAddress);
    const primaryPhoneNumber = contactValueOf(row.primaryPhoneNumber);
    const worksFor = worksForOf(row.worksFor);
    const matchedViaOrganizationAddress = viaOrganizationAddressOf(row.matchedViaOrganizationAddress);
    const lastChangedAt =
      typeof row.lastChangedAt === 'string' && !Number.isNaN(Date.parse(row.lastChangedAt)) ? row.lastChangedAt : undefined;
    return [
      {
        partyId: row.partyId,
        displayName: row.displayName,
        kind: typeof row.kind === 'string' ? row.kind : '',
        lifecycleStatus: typeof row.lifecycleStatus === 'string' ? row.lifecycleStatus : '',
        ...(matchedIdentifier === undefined ? {} : { matchedIdentifier }),
        ...(matchedLegalName === undefined ? {} : { matchedLegalName }),
        ...(primaryEmailAddress === undefined ? {} : { primaryEmailAddress }),
        ...(primaryPhoneNumber === undefined ? {} : { primaryPhoneNumber }),
        ...(worksFor === undefined ? {} : { worksFor, ...(row.worksForMore === true ? { worksForMore: true } : {}) }),
        ...(lastChangedAt === undefined ? {} : { lastChangedAt }),
        ...(typeof row.matchedViaOrganization === 'string' && row.matchedViaOrganization.length > 0
          ? { matchedViaOrganization: row.matchedViaOrganization }
          : {}),
        ...(matchedViaOrganizationAddress === undefined ? {} : { matchedViaOrganizationAddress }),
      },
    ];
  });
}

function viaOrganizationAddressOf(value: unknown): PartySummary['matchedViaOrganizationAddress'] {
  const row = (value ?? {}) as Record<string, unknown>;
  if (typeof row.organizationPartyId !== 'string' || typeof row.displayName !== 'string' || row.displayName.length === 0) return undefined;
  const more = typeof row.moreCount === 'number' && Number.isSafeInteger(row.moreCount) && row.moreCount > 0 ? row.moreCount : 0;
  return { organizationPartyId: row.organizationPartyId, displayName: row.displayName, moreCount: more };
}

function worksForOf(value: unknown): PartySummary['worksFor'] {
  const row = (value ?? {}) as Record<string, unknown>;
  return typeof row.partyId === 'string' && typeof row.displayName === 'string' && row.displayName.length > 0
    ? { partyId: row.partyId, displayName: row.displayName }
    : undefined;
}

/** "Rhein-Main Verlag" oder "Rhein-Main Verlag und weitere"; ohne Organisation nichts. */
export function organizationLineOf(party: Pick<PartySummary, 'worksFor' | 'worksForMore'>): string | undefined {
  if (party.worksFor === undefined) {
    return undefined;
  }
  return party.worksForMore === true ? `${party.worksFor.displayName} und weitere` : party.worksFor.displayName;
}

/** Warum eine Person getroffen hat, wenn nicht ueber ihren Namen: "über Rhein-Main Verlag". */
export function matchedViaOrganizationNotice(organizationName: string): string {
  return `über ${organizationName}`;
}

/** Die Einengung des Umschalters als Satz der Statusregion. */
export function kindViewNotice(kind: PartyKindFilter): string {
  return kind === 'person' ? 'Nur Personen.' : 'Nur Organisationen.';
}

/** Die Ueberschrift, wenn die Liste unter einer Art leer ist. */
export function kindEmptyHeading(kind: PartyKindFilter): string {
  return kind === 'person' ? 'Keine Personen in dieser Ansicht' : 'Keine Organisationen in dieser Ansicht';
}

/** Die letzte Aenderung als Datum, etwa "12.10.2026"; ohne brauchbaren Zeitpunkt nichts. */
export function lastChangedLabelOf(party: Pick<PartySummary, 'lastChangedAt'>): string | undefined {
  if (party.lastChangedAt === undefined || Number.isNaN(Date.parse(party.lastChangedAt))) {
    return undefined;
  }
  return new Date(party.lastChangedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });
}

/**
 * Die Kontaktzeile einer Listenzeile: E-Mail und Telefon, durch einen
 * Mittelpunkt getrennt, oder nichts. KLARTEXT und kein Link: die Zeile ist
 * selbst der Link zum Kontakt, und ein zweiter darin waere eine zweite
 * Aktion (5.2 Nr. 2).
 */
export function contactLineOf(party: Pick<PartySummary, 'primaryEmailAddress' | 'primaryPhoneNumber'>): string | undefined {
  const parts = [party.primaryEmailAddress, party.primaryPhoneNumber].filter((part): part is string => part !== undefined);
  return parts.length === 0 ? undefined : parts.join(' · ');
}

function contactPointsOf(value: unknown): readonly ContactPointView[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry: unknown) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    if (typeof row.contactPointUsageId !== 'string' || typeof row.value !== 'string') {
      return [];
    }
    return [
      {
        contactPointUsageId: row.contactPointUsageId,
        contactPointId: typeof row.contactPointId === 'string' ? row.contactPointId : '',
        value: row.value,
        usageCode: typeof row.usageCode === 'string' ? row.usageCode : '',
        isPreferred: row.isPreferred === true,
        verificationStatus: typeof row.verificationStatus === 'string' ? row.verificationStatus : '',
        sourceSystem: typeof row.sourceSystem === 'string' ? row.sourceSystem : '',
        // 0 heisst "unbekannt" und ist damit ein Sperrstand, den der Server
        // niemals fuehrt (die Spalte beginnt bei 1). Ein Befehl mit 0
        // antwortet deshalb mit 409 statt still zu ueberschreiben -- der
        // sichere Ausgang, wenn die Antwort das Feld nicht mitgebracht hat.
        lockVersion: typeof row.lockVersion === 'number' ? row.lockVersion : 0,
        contactPointLockVersion: typeof row.contactPointLockVersion === 'number' ? row.contactPointLockVersion : 0,
      },
    ];
  });
}

function endedContactPointsOf(value: unknown): readonly EndedContactPointView[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry: unknown) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    if (typeof row.contactPointUsageId !== 'string' || typeof row.value !== 'string') {
      return [];
    }
    return [
      {
        contactPointUsageId: row.contactPointUsageId,
        contactPointId: typeof row.contactPointId === 'string' ? row.contactPointId : '',
        value: row.value,
        usageCode: typeof row.usageCode === 'string' ? row.usageCode : '',
        endedAt: typeof row.endedAt === 'string' ? row.endedAt : '',
        ...(typeof row.endReasonCode === 'string' ? { endReasonCode: row.endReasonCode } : {}),
      },
    ];
  });
}

export interface ReadOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly send?: SendLike;
}

export async function fetchPartyList(
  options: ReadOptions & { readonly cursor?: string; readonly kind?: PartyKindFilter },
): Promise<PartyListResult> {
  const send = options.send ?? fetch;
  const parameters = [
    ...(options.cursor === undefined ? [] : [`cursor=${encodeURIComponent(options.cursor)}`]),
    ...(options.kind === undefined ? [] : [`kind=${encodeURIComponent(options.kind)}`]),
  ];
  const query = parameters.length === 0 ? '' : `?${parameters.join('&')}`;
  const response = await send(`${options.baseUrl}/parties${query}`, {
    headers: { authorization: `Bearer ${options.accessToken}` },
  });
  const payload = await response.json().catch(() => undefined);

  if (!response.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(payload), factsOf(response, payload)) };
  }

  const body = (payload ?? {}) as Record<string, unknown>;
  return {
    outcome: 'loaded',
    parties: summariesOf(payload),
    hasMore: body.hasMore === true,
    ...(typeof body.nextCursor === 'string' ? { nextCursor: body.nextCursor } : {}),
  };
}

/**
 * Die eigenen zuletzt bearbeiteten Kontakte (ST-SEA-001, Einstieg: Suche
 * zuerst): hoechstens zehn, juengste zuerst, ohne Cursor. Die Identitaet kommt
 * serverseitig aus dem Token; die Anfrage traegt nichts.
 */
export async function fetchRecentlyEditedParties(
  options: ReadOptions,
): Promise<{ readonly outcome: 'loaded'; readonly parties: readonly PartySummary[] } | { readonly outcome: 'rejected'; readonly message: PlainMessage }> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/party-recent-edits`, {
    headers: { authorization: `Bearer ${options.accessToken}` },
  });
  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(payload), factsOf(response, payload)) };
  }
  return { outcome: 'loaded', parties: summariesOf(payload) };
}

/**
 * Sucht Kontakte nach Name oder Kennung (ST-SEA-001 zweiter Schnitt).
 *
 * EIN POST MIT DEM BEGRIFF IM RUMPF und nie in der Adresse -- derselbe Weg wie
 * bei der Dublettenpruefung und aus demselben Grund (ADR-0044 Ziffer 3): der
 * Begriff ist ein Personendatum. Die Antwort hat die Form der Liste, damit
 * der Bildschirm eine Trefferzeile hat und nicht zwei.
 */
export async function searchParties(
  options: ReadOptions & {
    readonly term?: string | undefined;
    readonly filter?: SearchFilter | undefined;
    readonly cursor?: string;
    /** Bricht den Abruf ab, sobald eine juengere Eingabe ihn ersetzt. */
    readonly signal?: AbortSignal;
  },
): Promise<PartySearchResult> {
  const send = options.send ?? fetch;
  const filter = options.filter === undefined ? undefined : filterBody(options.filter);
  const response = await send(`${options.baseUrl}/party-search-results`, {
    method: 'POST',
    ...(options.signal === undefined ? {} : { signal: options.signal }),
    headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
    body: JSON.stringify({
      ...(options.term === undefined ? {} : { term: options.term }),
      ...(filter === undefined || Object.keys(filter).length === 0 ? {} : { filter }),
      ...(options.cursor === undefined ? {} : { cursor: options.cursor }),
    }),
  });
  const payload = await response.json().catch(() => undefined);

  if (response.status === 422 && JSON.stringify(payload ?? {}).includes('"fieldPath":"filter')) {
    // Ein Filter wurde abgewiesen, nicht der Begriff: der Satz ueber die
    // Laenge des Suchbegriffs waere hier falsch.
    return {
      outcome: 'rejected',
      message: {
        headline: 'Die Filter wurden nicht angenommen.',
        explanation: `LIAISON sucht mit höchstens ${MAXIMUM_FILTER_TERMS} Stichworten und nur mit Gesellschaften und Rollen aus der Auswahl.`,
        nextStep: 'Ändere die Filter und suche erneut.',
        retryable: false,
      },
    };
  }
  if (response.status === 422) {
    // Der Server hat den Begriff abgewiesen, obwohl der Bildschirm ihn vorher
    // prueft -- die beiden Regeln sind auseinandergelaufen, oder jemand hat
    // den Bildschirm umgangen. Ein Satz mit dem naechsten Schritt, kein
    // Wiederholweg: ein zweiter Versuch mit demselben Begriff aendert nichts.
    return {
      outcome: 'rejected',
      message: {
        headline: 'Der Suchbegriff wurde nicht angenommen.',
        explanation: SEARCH_LIMITS_SENTENCE,
        nextStep: 'Ändere den Suchbegriff und suche erneut.',
        retryable: false,
      },
    };
  }
  if (!response.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(payload), factsOf(response, payload)) };
  }

  const body = (payload ?? {}) as Record<string, unknown>;
  return {
    outcome: 'loaded',
    parties: summariesOf(payload),
    hasMore: body.hasMore === true,
    ...(typeof body.nextCursor === 'string' ? { nextCursor: body.nextCursor } : {}),
    ...(typeof body.matchCount === 'number' ? { matchCount: body.matchCount, matchCountCapped: body.matchCountCapped === true } : {}),
  };
}

/** Das Ergebnis der Suche: die Liste plus die Zahl der ersten Seite. */
export type PartySearchResult =
  | (Extract<PartyListResult, { outcome: 'loaded' }> & { readonly matchCount?: number; readonly matchCountCapped?: boolean })
  | Extract<PartyListResult, { outcome: 'rejected' }>;

/** Ein eigenes Haus als Auswahlwert der Facette; die Art benennt das Feld (M7). */
export interface SearchOptionContext {
  readonly operatingContextId: string;
  readonly displayName: string;
  readonly kind?: HausArt | null;
}

export type SearchOptionsResult =
  | { readonly outcome: 'loaded'; readonly operatingContexts: readonly SearchOptionContext[] }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export async function fetchSearchOptions(options: ReadOptions): Promise<SearchOptionsResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/party-search-options`, {
    headers: { authorization: `Bearer ${options.accessToken}` },
  });
  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(payload), factsOf(response, payload)) };
  }
  const rows = (payload as { operatingContexts?: unknown } | undefined)?.operatingContexts;
  const operatingContexts = Array.isArray(rows)
    ? rows.flatMap((row): SearchOptionContext[] => {
        const entry = row as Record<string, unknown>;
        return typeof entry.operatingContextId === 'string' && typeof entry.displayName === 'string'
          ? [{ operatingContextId: entry.operatingContextId, displayName: entry.displayName, kind: hausArtOf(entry.kind) }]
          : [];
      })
    : [];
  return { outcome: 'loaded', operatingContexts };
}

export async function fetchParty(options: ReadOptions & { readonly partyId: string }): Promise<PartyDetailResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}`, {
    headers: { authorization: `Bearer ${options.accessToken}` },
  });
  const payload = await response.json().catch(() => undefined);

  if (response.status === 404) {
    return { outcome: 'not-found' };
  }
  if (!response.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(payload), factsOf(response, payload)) };
  }

  const body = (payload ?? {}) as Record<string, unknown>;
  if (typeof body.partyId !== 'string' || typeof body.displayName !== 'string') {
    // Eine 200 ohne die tragenden Felder ist keine Auskunft von LIAISON,
    // sondern etwas, das sich dafuer ausgibt -- derselbe Fall wie eine
    // 200 mit HTML in fetchAccessSnapshot.
    return { outcome: 'rejected', message: plainMessageFor(undefined, { ...factsOf(response, payload), answered: false }) };
  }

  const person = (body.person ?? undefined) as Record<string, unknown> | undefined;
  const organization = (body.organization ?? undefined) as Record<string, unknown> | undefined;

  return {
    outcome: 'loaded',
    party: {
      partyId: body.partyId,
      displayName: body.displayName,
      kind: typeof body.kind === 'string' ? body.kind : '',
      lifecycleStatus: typeof body.lifecycleStatus === 'string' ? body.lifecycleStatus : '',
      createdAt: typeof body.createdAt === 'string' ? body.createdAt : '',
      updatedAt: typeof body.updatedAt === 'string' ? body.updatedAt : '',
      ...(typeof body.lockVersion === 'number' ? { lockVersion: body.lockVersion } : {}),
      ...(person === undefined
        ? {}
        : {
            person: {
              ...(typeof person.givenName === 'string' ? { givenName: person.givenName } : {}),
              ...(typeof person.familyName === 'string' ? { familyName: person.familyName } : {}),
              lockVersion: typeof person.lockVersion === 'number' ? person.lockVersion : 0,
            },
          }),
      ...(organization !== undefined && typeof organization.legalName === 'string'
        ? {
            organization: {
              legalName: organization.legalName,
              ...(typeof organization.tradeName === 'string' ? { tradeName: organization.tradeName } : {}),
              // 0 heisst "unbekannt": ein Sperrstand, den der Server nie
              // fuehrt. Der Organisationsbildschirm behandelt ihn als
              // unvollstaendige Antwort mit Wiederholweg, BEVOR ein Befehl
              // entsteht -- der Parser wiese die 0 als 422 an einem Feld ab,
              // das kein Bildschirm zeigt (Befund des Cloud-Reviews an #226).
              lockVersion: typeof organization.lockVersion === 'number' ? organization.lockVersion : 0,
            },
          }
        : {}),
      emailAddresses: contactPointsOf(body.emailAddresses),
      phoneNumbers: contactPointsOf(body.phoneNumbers),
      endedEmailAddresses: endedContactPointsOf(body.endedEmailAddresses),
      endedPhoneNumbers: endedContactPointsOf(body.endedPhoneNumbers),
      websites: contactPointsOf(body.websites),
      endedWebsites: endedContactPointsOf(body.endedWebsites),
      endedContactPointsTruncated: body.endedContactPointsTruncated === true,
      additionalContactPointCount:
        typeof body.additionalContactPointCount === 'number' ? body.additionalContactPointCount : 0,
      otherContactPointCount: typeof body.otherContactPointCount === 'number' ? body.otherContactPointCount : 0,
      ...visibilityOf(body.visibility),
    },
  };
}

/**
 * Die Sichtbarkeit aus der Detailantwort (ADR-0064). Nur eine bekannte Klasse
 * zaehlt; alles andere -- auch eine aeltere API ohne das Feld -- ergibt KEINE
 * Angabe, und der Kopf zeigt dann kein Badge statt eines geratenen.
 */
function visibilityOf(raw: unknown): { visibility?: NonNullable<PartyDetail['visibility']> } {
  if (typeof raw !== 'object' || raw === null) return {};
  const { scope, ownerHouseName, ownerHouseKind } = raw as Record<string, unknown>;
  if (scope !== 'groupwide' && scope !== 'house') return {};
  return {
    visibility: {
      scope,
      ownerHouseName: scope === 'house' && typeof ownerHouseName === 'string' && ownerHouseName.length > 0 ? ownerHouseName : null,
      ownerHouseKind: scope === 'house' ? hausArtOf(ownerHouseKind) : null,
    },
  };
}

/** Die Adresse eines Kontakts in der Oberflaeche. Eine Stelle, nicht drei. */
export function partyHref(partyId: string): string {
  return `/kontakte/${encodeURIComponent(partyId)}`;
}

/** Die Adresse der Kontaktwegpflege (ST-PTY-002). */
export function contactPointsHref(partyId: string): string {
  return `${partyHref(partyId)}/kontaktwege`;
}

/** Die Adresse der Kennungspflege (ST-PTY-003). */
export function identifiersHref(partyId: string): string {
  return `${partyHref(partyId)}/kennungen`;
}

/** Die Adresse der Organisationsnamen-Pflege (ST-PTY-003, zweiter Schnitt). */
export function organizationHref(partyId: string): string {
  return `${partyHref(partyId)}/organisation`;
}
